import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HttpService } from 'src/app/http.service';

@Component({
  selector: 'app-player-agent-log',
  templateUrl: './player-agent-log.component.html',
  styleUrls: ['./player-agent-log.component.scss']
})
export class PlayerAgentLogComponent {

  public data: any = { type: 'last7' };
  public logs: any[] = [];
  public dates: any[] = [];
  public is_loading_logs = false;
  public is_loading_detail = false;
  public today: string = new Date().toISOString().split('T')[0];
  public custom_from: string = '';
  public custom_to: string = '';
  public commissionDetail: any = null;
  public selected_log: any = null;
  public configuration: any = {};

  @Output() closeEvent = new EventEmitter();

  @Input() set setPlayer(player: any) {
    if (!player) return;
    this._agentCode = player.code;
    const now = new Date();
    this.custom_from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    this.custom_to = this.today;
    this.generateDates();
  }

  private _agentCode: string = '';

  constructor(private http: HttpService) {
    this.http.post('/admin/company/setting').then((rtn: any) => {
      this.configuration = rtn.setting || {};
    });
  }

  getDateRange(): { from: string; to: string } {
    const now = new Date();
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    switch (this.data.type) {
      case 'today': return { from: this.today, to: this.today };
      case 'yesterday': { const d = new Date(now); d.setDate(d.getDate() - 1); return { from: fmt(d), to: fmt(d) }; }
      case 'last7': { const d = new Date(now); d.setDate(d.getDate() - 6); return { from: fmt(d), to: this.today }; }
      case 'last30': { const d = new Date(now); d.setDate(d.getDate() - 29); return { from: fmt(d), to: this.today }; }
      case 'this_month': return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), to: this.today };
      case 'last_month': {
        const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const last = new Date(now.getFullYear(), now.getMonth(), 0);
        return { from: fmt(first), to: fmt(last) };
      }
      default: return { from: '', to: '' };
    }
  }

  async filter() {
    this.is_loading_logs = true;
    this.commissionDetail = null;
    this.selected_log = null;

    const payload: any = { type: this.data.type, search: this._agentCode };

    if (this.data.type === 'week') {
      payload.date = this.dates.find(d => d.value === this.data.week_number);
    } else if (this.data.type === 'day') {
      payload.date = this.data.date;
      payload.type = 'day';
    } else if (this.data.type === 'custom') {
      if (!this.custom_from || !this.custom_to) { this.is_loading_logs = false; return; }
      payload.from = this.custom_from;
      payload.to = this.custom_to;
    } else {
      const range = this.getDateRange();
      payload.type = 'custom';
      payload.from = range.from;
      payload.to = range.to;
    }

    const rtn: any = await this.http.post('/admin/agent-log', payload);
    this.logs = rtn.status ? (rtn.agents || []) : [];
    this.is_loading_logs = false;
  }

  async openDetail(log: any) {
    this.selected_log = log;
    this.commissionDetail = null;
    this.is_loading_detail = true;
    const rtn: any = await this.http.post('/admin/commission-detail', {
      from: log.from, to: log.to, player_id: log.id
    });
    if (rtn.status) {
      this.commissionDetail = rtn;
      if (rtn.report?.detail?.length > 0 && rtn.report.detail[0].breakdown) {
        try {
          const stored = typeof rtn.report.detail[0].breakdown === 'string'
            ? JSON.parse(rtn.report.detail[0].breakdown)
            : rtn.report.detail[0].breakdown;
          if (stored.report) this.commissionDetail.breakdown = stored.report;
        } catch (e) { }
      }
    }
    this.is_loading_detail = false;
  }

  switchType() {
    if (this.data.type === 'day') this.data.date = this.today;
    this.filter();
  }

  periodLabel(): string {
    if (this.data.type === 'week') {
      const match = this.dates.find(d => d.value === this.data.week_number);
      return match ? match.label : '';
    }
    if (this.data.type === 'day') return this.data.date || '';
    if (this.data.type === 'custom') return (this.custom_from || '') + ' to ' + (this.custom_to || '');
    const range = this.getDateRange();
    return range.from + ' to ' + range.to;
  }

  generateDates() {
    this.dates = [];
    const today = new Date();
    const day = today.getDay();
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - day - 7);
    for (let i = 0; i < 13; i++) {
      const weekStart = new Date(lastSunday);
      weekStart.setDate(lastSunday.getDate() - i * 7);
      const firstJan = new Date(weekStart.getFullYear(), 0, 1);
      const days = Math.floor((weekStart.getTime() - firstJan.getTime()) / 86400000);
      const weekNumber = Math.floor((days + firstJan.getDay()) / 7) + 1;
      const monday = new Date(weekStart); monday.setDate(weekStart.getDate() + 1);
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      const fmt = (d: Date) => ('0' + d.getDate()).slice(-2) + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + d.getFullYear();
      this.dates.push({
        year: weekStart.getFullYear(), week_number: weekNumber,
        value: weekNumber + '-' + weekStart.getFullYear(),
        label: 'Week ' + weekNumber + ': ' + fmt(monday) + ' to ' + fmt(sunday),
        from: fmt(monday), to: fmt(sunday)
      });
    }
    this.data.week_number = this.dates[0].value;
    this.data.date = this.today;
    this.filter();
  }

  sum(key: string): number {
    return this.logs.reduce((acc, log) => acc + parseFloat(log[key] ?? 0), 0);
  }

  agentFormulaText(day: any): string {
    const b = day?.breakdown || {};
    if (b.mode !== 'wallet_balance') return day?.formula_param || '-';
    const rawProfit = Number(b.raw_profit || 0);
    const rolling = Number(b.rolling_deduction ?? b.total_rolling_paid ?? 0);
    const bucket = Number(b.bucket_deduction || 0);
    const rate = Number(b.rate || 0);
    const gross = Number(b.gross || 0);
    const childGross = Array.isArray(b.deductions)
      ? b.deductions.reduce((sum: number, d: any) => sum + Number(d.amount || 0), 0)
      : 0;
    const net = Number(day?.commission || 0);
    const base = `(${rawProfit.toFixed(3)} - ${rolling.toFixed(3)} - ${bucket.toFixed(3)}) × ${rate}%`;
    if (Math.abs(childGross) >= 0.001) {
      return `${base} - child agents ${childGross.toFixed(3)} = ${net.toFixed(3)}`;
    }
    return `${base} = ${gross.toFixed(3)}`;
  }

  close() { this.closeEvent.emit(); }
}
